import React, { createContext, useContext, useEffect, useState } from 'react';
import * as Sentry from '@sentry/react';
import { getAuthTokens, clearAuthTokens } from '@/api';
import { getCurrentUser } from '@/api';
import { clearMcpAuthCache } from '@/api/lib/client';
import type { UserProfile } from '@/api/auth/auth.types';

interface AuthContextType {
    isAuthenticated: boolean;
    isLoading: boolean;
    user: UserProfile | null;
    logout: () => void;
    setAuthData: () => Promise<void>;
    refreshUser: () => Promise<void>;
    hasPermission: (permission: keyof UserProfile['permissions']) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
    const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [user, setUser] = useState<UserProfile | null>(null);
    const [previousOrgId, setPreviousOrgId] = useState<string | null>(null);

    // Function to fetch user data
    const fetchUserData = async (showLoading = true) => {
        if (showLoading) setIsLoading(true);
        try {
            const response = await getCurrentUser();
            // console.log('User data:', response.data);

            if (response.error) {
                // console.error('Error fetching user data:', response.error);
                if (response.error.includes('authentication') || response.error.includes('401')) {
                    handleLogout();
                }
                return;
            }

            // Check if organization changed
            if (response.data && previousOrgId && previousOrgId !== response.data.organization_id) {
                console.log('[Auth] Organization changed, clearing MCP cache');
                clearMcpAuthCache();
            }
            
            // Update previous org ID
            if (response.data) {
                setPreviousOrgId(response.data.organization_id);
            }

            setUser(response.data ?? null);
            setIsAuthenticated(true);
            
            // Set Sentry user context for error tracking
            if (response.data) {
                Sentry.setUser({
                    id: response.data.id,
                    email: response.data.email,
                    username: `${response.data.first_name} ${response.data.last_name}`,
                });
                
                // Add additional context
                Sentry.setContext('user_details', {
                    organization_id: response.data.organization_id,
                    organization_name: response.data.organization_name,
                    project_id: response.data.project_id,
                    role: response.data.role,
                    status: response.data.status,
                });
                
                // Add permissions as tags for easier filtering
                Sentry.setTags({
                    'user.role': response.data.role,
                    'user.status': response.data.status,
                });
            }
        } catch (error) {
            // console.error('Error in fetchUserData:', error);
            handleLogout();
        } finally {
            setIsLoading(false);
        }
    };

    // Initialize auth state
    useEffect(() => {
        const tokens = getAuthTokens();
        if (tokens) {
            fetchUserData();
        } else {
            setIsLoading(false);
            setIsAuthenticated(false);
        }
    }, []);

    // Function to handle logout
    const handleLogout = () => {
        // First clear tokens to prevent any auto-refresh attempts
        clearAuthTokens();
        // Clear MCP auth cache on logout
        clearMcpAuthCache();
        // Then update state
        setIsAuthenticated(false);
        setUser(null);
        setPreviousOrgId(null);
        
        // Clear Sentry user context on logout
        Sentry.setUser(null);
    };

    const setAuthData = async () => {
        await fetchUserData();
    };

    const refreshUser = async () => {
        await fetchUserData(false);
    };

    // Helper function to check permissions
    const hasPermission = (permission: keyof UserProfile['permissions']): boolean => {
        if (!user?.permissions) return false;
        const permissionValue = user.permissions[permission];
        return permissionValue === 'write' || permissionValue === 'read';
    };

    const contextValue = React.useMemo(() => ({
        isAuthenticated,
        isLoading,
        user,
        logout: handleLogout,
        setAuthData,
        refreshUser,
        hasPermission,
    }), [isAuthenticated, isLoading, user]);

    return (
        <AuthContext.Provider value={contextValue}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}
